/**
 * Admin dashboard — invite-only auth with role-based permissions.
 *
 * Access: personal email + password. Users are added only via `POST
 * /api/admin/users/invite` by an existing owner. The very first owner is
 * bootstrapped with `POST /api/admin/auth/setup-first-owner`, which self-
 * disables after the first successful call.
 *
 * Route: `/admin` — not linked from anywhere. Users get here via the invite
 * email or by bookmarking. Direct URL still requires login.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, ChevronRight, TrendingUp, MapPin, LogOut, UserPlus,
  Shield, ShieldCheck, User as UserIcon, Info, Zap, Star, HelpCircle,
  Upload, FileText, AlertTriangle, Trash2, Mail, Copy, Heart, Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const money = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TOKEN_KEY = "teh_admin_token";
const getTok = () => localStorage.getItem(TOKEN_KEY) || "";
const setTok = (t) => localStorage.setItem(TOKEN_KEY, t);
const clearTok = () => localStorage.removeItem(TOKEN_KEY);
const authHeader = () => ({ Authorization: `Bearer ${getTok()}` });

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { "Content-Type": "application/json", ...authHeader(), ...(opts.headers || {}) },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || `Error ${r.status}`);
  return j;
}

// ==================== Login / setup gate ====================

function AuthGate({ onAuthed }) {
  const [mode, setMode] = useState(null); // login | setup-first | complete-invite | reset
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [showForgot, setShowForgot] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Check whether the first-owner bootstrap has been run
        const r = await fetch(`${API}/admin/auth/setup-status`);
        const j = await r.json();
        if (!j.initialized) { setMode("setup-first"); return; }
        const params = new URLSearchParams(window.location.search);
        if (params.get("reset")) {
          setMode("reset");
          setForm((f) => ({ ...f, email: params.get("email") || "" }));
        } else if (params.get("token")) {
          setMode("complete-invite");
          setForm((f) => ({ ...f, email: params.get("email") || "" }));
        } else {
          setMode("login");
        }
      } catch {
        setMode("login");
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "setup-first") {
        const j = await api("/admin/auth/setup-first-owner", { method: "POST", body: form });
        setTok(j.access_token);
        toast.success("First owner account created.");
        onAuthed();
      } else if (mode === "login") {
        const j = await api("/admin/auth/login", { method: "POST",
          body: { email: form.email, password: form.password } });
        setTok(j.access_token);
        onAuthed();
      } else if (mode === "complete-invite") {
        const params = new URLSearchParams(window.location.search);
        const j = await api("/admin/auth/complete-invite", { method: "POST",
          body: { invite_token: params.get("token"), password: form.password } });
        setTok(j.access_token);
        window.history.replaceState({}, "", "/admin");
        toast.success("Password set — welcome.");
        onAuthed();
      } else if (mode === "reset") {
        const params = new URLSearchParams(window.location.search);
        const j = await api("/admin/auth/reset-password", { method: "POST",
          body: { reset_token: params.get("reset"), password: form.password } });
        setTok(j.access_token);
        window.history.replaceState({}, "", "/admin");
        toast.success("Password updated — you're signed in.");
        onAuthed();
      }
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const requestReset = async () => {
    if (!form.email) { toast.error("Enter your email above first"); return; }
    setBusy(true);
    try {
      await api("/admin/auth/request-reset", { method: "POST", body: { email: form.email } });
      toast.success("If that email exists, a reset link is on its way (check spam).");
      setShowForgot(false);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (!mode) return <div className="min-h-screen bg-brand-bg" />;

  const title = mode === "setup-first" ? "Set up the first owner account"
             : mode === "complete-invite" ? "Set your password to accept the invite"
             : mode === "reset" ? "Choose a new password"
             : "Admin sign in";
  const cta = mode === "setup-first" ? "Create owner account"
           : mode === "complete-invite" ? "Set password"
           : mode === "reset" ? "Save & sign in"
           : "Sign in";

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg px-6">
      <form onSubmit={submit} className="max-w-sm w-full bg-white rounded-2xl border border-brand-navy/10 p-8" data-testid="admin-auth">
        <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center mb-3">
          <ShieldCheck className="w-5 h-5 text-brand-green" />
        </div>
        <h1 className="font-serif text-2xl text-brand-deep mb-2">{title}</h1>
        <p className="text-sm text-brand-muted mb-5">
          {mode === "setup-first" ? "This is only shown once — you'll be the first owner." :
           mode === "complete-invite" ? "You were invited to join the console." :
           mode === "reset" ? "Enter a new password to complete the reset." :
           "Enter the email and password from your invite."}
        </p>
        {(mode === "setup-first" || mode === "complete-invite") && (
          <label className="block mb-2 text-xs uppercase tracking-[0.14em] font-semibold text-brand-muted">Full name</label>
        )}
        {mode === "setup-first" && (
          <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border border-brand-navy/20 rounded-full px-4 py-3 text-sm mb-3" placeholder="Your name" data-testid="admin-name" />
        )}
        {mode !== "complete-invite" && mode !== "reset" && (
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full border border-brand-navy/20 rounded-full px-4 py-3 text-sm mb-3" placeholder="Email" data-testid="admin-email" autoFocus />
        )}
        {(mode === "complete-invite" || mode === "reset") && (
          <div className="w-full border border-brand-navy/10 bg-brand-bg rounded-full px-4 py-2.5 text-sm mb-3 text-brand-muted">{form.email}</div>
        )}
        <input required minLength={8} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full border border-brand-navy/20 rounded-full px-4 py-3 text-sm mb-3" placeholder="Password (at least 8 characters)" data-testid="admin-pw" />
        <button type="submit" disabled={busy}
          className="w-full bg-brand-green hover:bg-brand-greenDark text-white font-semibold py-3 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="admin-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : cta}
        </button>
        {mode === "login" && (
          <div className="text-center mt-4">
            <button type="button" onClick={requestReset} disabled={busy}
              data-testid="admin-forgot"
              className="text-xs text-brand-muted hover:text-brand-green underline">
              Forgot password? Email me a reset link
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

// ==================== Role explainer (plain English) ====================

const ROLE_INFO = {
  owner: {
    title: "Owner",
    tagline: "Full control — think 'CEO login'.",
    can: [
      "See every dashboard number and drill into any ride",
      "Invite new teammates and set their permissions",
      "Promote or demote anyone (except the last remaining owner — that's you)",
      "Turn off (disable) accounts if a teammate leaves",
      "Everything a Dispatcher can do, plus manage the team",
    ],
    cant: ["Nothing on the app is off-limits."],
    tone: "bg-emerald-50 border-emerald-300 text-emerald-900",
  },
  dispatcher: {
    title: "Dispatcher",
    tagline: "Runs the day-to-day — books, assigns, collects.",
    can: [
      "See the live dashboard (today's rides, revenue, action needed)",
      "Look up any patient or guest bookee and see their full ride history",
      "Assign drivers, change holds, send payment links, charge saved cards",
      "Release holds + confirm cash/Zelle payments after a ride",
      "View driver locations and performance",
    ],
    cant: [
      "Cannot invite new teammates or change other people's permissions",
      "Cannot disable or delete other accounts",
    ],
    tone: "bg-blue-50 border-blue-300 text-blue-900",
  },
  viewer: {
    title: "Viewer",
    tagline: "Look but don't touch — great for accountants/investors.",
    can: [
      "See the dashboard (revenue, ride counts, outstanding balances)",
      "Read patient records and ride history",
      "See driver performance",
    ],
    cant: [
      "Cannot book, assign, or cancel rides",
      "Cannot charge cards or send payment links",
      "Cannot invite anyone or change permissions",
    ],
    tone: "bg-slate-50 border-slate-300 text-slate-800",
  },
};

function RoleExplainer({ role, onClose }) {
  const info = ROLE_INFO[role] || ROLE_INFO.viewer;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <div className={`max-w-md w-full bg-white rounded-2xl border-2 p-6 ${info.tone}`}
           onClick={(e) => e.stopPropagation()} data-testid={`role-explainer-${role}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70">Role</div>
            <h3 className="font-serif text-2xl">{info.title}</h3>
            <p className="text-sm mt-1 opacity-80">{info.tagline}</p>
          </div>
          <button onClick={onClose} className="text-xs px-3 py-1 rounded-full bg-white/60 hover:bg-white">Close</button>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold mb-1 opacity-70">Can do</div>
          <ul className="text-sm space-y-1">
            {info.can.map((c) => <li key={c} className="flex items-start gap-2"><span className="mt-1">✓</span><span>{c}</span></li>)}
          </ul>
        </div>
        {info.cant.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold mb-1 opacity-70">Cannot do</div>
            <ul className="text-sm space-y-1 opacity-80">
              {info.cant.map((c) => <li key={c} className="flex items-start gap-2"><span className="mt-1">•</span><span>{c}</span></li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Stat tile ====================

function StatCard({ label, value, sub, tone = "default", testid }) {
  const toneCls = {
    default: "bg-white border-brand-navy/10 text-brand-deep",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    red: "bg-red-50 border-red-200 text-red-900",
  }[tone];
  return (
    <div className={`border rounded-2xl p-5 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">{label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
      {sub && <div className="text-xs opacity-75 mt-1">{sub}</div>}
    </div>
  );
}

// ==================== Users panel (owner-only) ====================

function UsersPanel({ me, onReload }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "dispatcher" });
  const [busy, setBusy] = useState(false);
  const [explainRole, setExplainRole] = useState(null);

  const load = useCallback(async () => {
    try {
      const j = await api("/admin/users");
      setUsers(j.users); setRoles(j.roles);
    } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const j = await api("/admin/users/invite", { method: "POST", body: inviteForm });
      toast.success(`Invite sent to ${j.user.email}`);
      setInviteForm({ email: "", name: "", role: "dispatcher" });
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const patchUser = async (u, patch) => {
    try {
      await api(`/admin/users/${u.id}`, { method: "PATCH", body: patch });
      toast.success("Updated");
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="users-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl text-brand-deep">Team access</h2>
        <Shield className="w-5 h-5 text-brand-green" />
      </div>

      <form onSubmit={submitInvite} className="bg-brand-bg rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted">Invite a teammate</div>
          <div className="text-[11px] text-brand-muted">Only <b>@1sttouchenterprise.com</b> emails accepted</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input required type="text" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
            className="border border-brand-navy/20 rounded-full px-3 py-2 text-sm" placeholder="Name" data-testid="invite-name" />
          <input required type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            className="border border-brand-navy/20 rounded-full px-3 py-2 text-sm" placeholder="name@1sttouchenterprise.com" data-testid="invite-email" />
          <div className="relative flex">
            <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              className="flex-1 border border-brand-navy/20 rounded-l-full px-3 py-2 text-sm bg-white capitalize" data-testid="invite-role">
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="button" onClick={() => setExplainRole(inviteForm.role)}
              className="px-3 border border-l-0 border-brand-navy/20 rounded-r-full bg-white text-brand-green hover:bg-brand-bg"
              data-testid="invite-role-explain" title="What can this role do?">
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
          <button type="submit" disabled={busy} data-testid="invite-submit"
            className="bg-brand-green hover:bg-brand-greenDark text-white text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-40 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Send invite
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {roles.map((r) => (
            <button key={r} type="button" onClick={() => setExplainRole(r)}
              className="text-[11px] px-2.5 py-1 rounded-full border border-brand-navy/15 bg-white hover:bg-brand-bg text-brand-deep capitalize inline-flex items-center gap-1"
              data-testid={`explain-${r}`}>
              <Info className="w-3 h-3" /> What is a {r}?
            </button>
          ))}
        </div>
      </form>

      <ul className="space-y-2">
        {users.map((u) => (
          <li key={u.id} className={`border rounded-xl p-3 flex items-center gap-3 flex-wrap ${u.disabled ? "opacity-50 bg-slate-50" : "bg-white"}`} data-testid={`user-row-${u.id}`}>
            <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center shrink-0">
              <UserIcon className="w-4 h-4 text-brand-green" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-brand-deep truncate">{u.name} <span className="text-brand-muted font-normal">· {u.email}</span></div>
              <div className="text-[11px] text-brand-muted flex items-center gap-2">
                <span className="uppercase">{u.role}</span>
                <button onClick={() => setExplainRole(u.role)} className="text-brand-green hover:underline inline-flex items-center gap-0.5" data-testid={`explain-row-${u.id}`}>
                  <HelpCircle className="w-3 h-3" /> what does this role do?
                </button>
                · {u.invite_status === "pending" ? "Invite pending" : "Active"}
                {u.last_login_at ? ` · Last login ${new Date(u.last_login_at).toLocaleDateString()}` : ""}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <select value={u.role} onChange={(e) => patchUser(u, { role: e.target.value })}
                className="border border-brand-navy/20 rounded-full px-3 py-1.5 text-xs bg-white capitalize" data-testid={`role-${u.id}`}>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button onClick={() => patchUser(u, { disabled: !u.disabled })}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full ${u.disabled ? "bg-emerald-100 text-emerald-800" : "bg-red-50 text-red-700"}`}
                data-testid={`toggle-${u.id}`}>
                {u.disabled ? "Re-enable" : "Disable"}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {explainRole && <RoleExplainer role={explainRole} onClose={() => setExplainRole(null)} />}
    </section>
  );
}

// ==================== Caregivers panel (Home Care) ====================

const CAREGIVER_TYPES = ["RN", "LPN", "CNA", "HHA", "PCA", "Companion", "Therapist"];
const CERT_PRESETS = ["BLS", "CPR", "TB test", "Drug screen", "Background check", "State license"];

function daysUntil(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  return Math.round((then - Date.now()) / 86400000);
}

function CertUploadForm({ caregiverId, onDone }) {
  const [form, setForm] = useState({ name: "BLS", expires_at: "", issuer: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name);
      if (form.expires_at) fd.append("expires_at", form.expires_at);
      if (form.issuer) fd.append("issuer", form.issuer);
      const r = await fetch(`${API}/homecare/admin/caregivers/${caregiverId}/certifications`, {
        method: "POST",
        headers: { "X-Dispatcher-Auth": "Enterpr!Se", ...authHeader() },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `Upload failed (${r.status})`);
      toast.success("Certification uploaded");
      setFile(null);
      setForm({ name: "BLS", expires_at: "", issuer: "" });
      onDone && onDone();
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-3 space-y-2" data-testid={`cert-upload-form-${caregiverId}`}>
      <div className="grid grid-cols-2 gap-2">
        <select value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="px-2 py-1.5 text-xs rounded border border-brand-navy/15"
          data-testid={`cert-type-${caregiverId}`}>
          {CERT_PRESETS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          data-testid={`cert-expires-${caregiverId}`}
          className="px-2 py-1.5 text-xs rounded border border-brand-navy/15" placeholder="Expires" />
      </div>
      <input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })}
        placeholder="Issuer (optional)"
        data-testid={`cert-issuer-${caregiverId}`}
        className="w-full px-2 py-1.5 text-xs rounded border border-brand-navy/15" />
      <div className="flex items-center gap-2">
        <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid={`cert-file-${caregiverId}`}
          className="text-xs flex-1" />
        <button disabled={busy || !file} data-testid={`cert-submit-${caregiverId}`}
          className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5 rounded-full">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          Upload
        </button>
      </div>
    </form>
  );
}

function CaregiverRow({ cg, onChanged }) {
  const [open, setOpen] = useState(false);
  const [certs, setCerts] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);

  const loadCerts = useCallback(async () => {
    try {
      const j = await api(`/homecare/admin/caregivers/${cg.id}/certifications`, {
        headers: { "X-Dispatcher-Auth": "Enterpr!Se" },
      });
      setCerts(j.certifications || []);
    } catch (e) { toast.error(e.message); }
  }, [cg.id]);

  const loadLink = useCallback(async () => {
    try {
      const j = await api(`/homecare/admin/caregivers/${cg.id}/pwa-link`, {
        headers: { "X-Dispatcher-Auth": "Enterpr!Se" },
      });
      setLinkInfo(j);
    } catch (e) { toast.error(e.message); }
  }, [cg.id]);

  const sendInvite = async () => {
    try {
      const j = await api(`/homecare/admin/caregivers/${cg.id}/send-invite`, {
        method: "POST",
        headers: { "X-Dispatcher-Auth": "Enterpr!Se" },
      });
      toast.success(`Invite ${j.status} to ${j.email} · code ${j.invite_code}`);
      setLinkInfo({ token: j.pwa_url.split("/").pop(), invite_code: j.invite_code, caregiver_name: cg.name });
    } catch (e) { toast.error(e.message || "Send failed"); }
  };

  const deleteCert = async (cid) => {
    if (!window.confirm("Remove this certification?")) return;
    try {
      await api(`/homecare/admin/caregivers/${cg.id}/certifications/${cid}`, {
        method: "DELETE",
        headers: { "X-Dispatcher-Auth": "Enterpr!Se" },
      });
      toast.success("Removed");
      loadCerts();
    } catch (e) { toast.error(e.message); }
  };

  const site = window.location.origin;

  const toggle = async () => {
    setOpen((v) => !v);
    if (!open) { loadCerts(); loadLink(); }
  };

  return (
    <div className="bg-white border border-brand-navy/10 rounded-xl overflow-hidden" data-testid={`caregiver-row-${cg.id}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-brand-bg text-left">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Heart className="w-4 h-4 text-emerald-700" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-brand-deep truncate">{cg.name}</div>
            <div className="text-xs text-brand-muted truncate">{cg.caregiver_type} · {cg.email}{cg.invite_code ? ` · code ${cg.invite_code}` : ""}</div>
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-brand-muted transition ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-brand-navy/10 p-4 space-y-4 bg-brand-bg/40">
          <div className="flex flex-wrap gap-2">
            <button onClick={sendInvite} data-testid={`send-invite-${cg.id}`}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 rounded-full">
              <Mail className="w-3.5 h-3.5" /> Email invite
            </button>
            {linkInfo && (
              <button onClick={() => { navigator.clipboard.writeText(`${site}/caregiver/${linkInfo.token}`); toast.success("Link copied"); }}
                data-testid={`copy-link-${cg.id}`}
                className="inline-flex items-center gap-1.5 bg-white border border-brand-navy/15 hover:bg-brand-bg text-xs font-medium px-3 py-1.5 rounded-full">
                <Copy className="w-3.5 h-3.5" /> Copy PWA link
              </button>
            )}
            {linkInfo?.invite_code && (
              <span className="inline-flex items-center gap-1.5 bg-white border border-brand-navy/15 text-xs px-3 py-1.5 rounded-full font-mono tracking-widest"
                data-testid={`invite-code-${cg.id}`}>
                {linkInfo.invite_code}
              </span>
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-muted mb-2">Certifications</div>
            <div className="space-y-2 mb-3" data-testid={`certs-list-${cg.id}`}>
              {(certs || []).length === 0 && (
                <div className="text-xs text-brand-muted italic">No certifications on file yet.</div>
              )}
              {(certs || []).map((c) => {
                const days = daysUntil(c.expires_at);
                const expired = days !== null && days < 0;
                const warning = days !== null && days >= 0 && days <= 30;
                return (
                  <div key={c.id} className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm ${expired ? "border-red-300 bg-red-50" : warning ? "border-amber-300 bg-amber-50" : "border-brand-navy/10 bg-white"}`}
                    data-testid={`cert-${c.id}`}>
                    <FileText className="w-4 h-4 text-brand-muted flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-brand-deep font-medium truncate">{c.name}{c.issuer ? ` · ${c.issuer}` : ""}</div>
                      <div className="text-[11px] text-brand-muted">
                        {c.expires_at ? (
                          expired ? <span className="text-red-700 font-semibold">Expired {Math.abs(days)}d ago</span>
                            : warning ? <span className="text-amber-800 font-semibold">Expires in {days}d</span>
                            : <>Expires {c.expires_at}</>
                        ) : "No expiry"}
                      </div>
                    </div>
                    <a href={`${API}/homecare/admin/caregivers/${cg.id}/certifications/${c.id}/download?auth=${encodeURIComponent("Enterpr!Se")}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`cert-view-${c.id}`}
                      className="text-xs text-emerald-700 hover:underline">View</a>
                    <button onClick={() => deleteCert(c.id)} data-testid={`cert-delete-${c.id}`}
                      className="text-brand-muted hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
            <CertUploadForm caregiverId={cg.id} onDone={() => { loadCerts(); onChanged && onChanged(); }} />
          </div>
        </div>
      )}
    </div>
  );
}

function CaregiversPanel() {
  const [list, setList] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState({ email: "", name: "", phone: "", caregiver_type: "CNA" });
  const [busy, setBusy] = useState(false);

  const dispatcherHdr = { "X-Dispatcher-Auth": "Enterpr!Se" };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api("/homecare/admin/caregivers", { headers: dispatcherHdr }),
        api("/homecare/admin/certifications/expiring-soon?days=30", { headers: dispatcherHdr }),
      ]);
      setList(a.caregivers || []);
      setExpiring(b.expiring || []);
    } catch (e) { toast.error(e.message || "Failed to load caregivers"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!invite.email || !invite.name) { toast.error("Name & email required"); return; }
    setBusy(true);
    try {
      const r = await api("/homecare/admin/caregivers/invite", {
        method: "POST", body: invite, headers: dispatcherHdr,
      });
      toast.success(`Added ${invite.name} · code ${r.caregiver.invite_code}`);
      setInvite({ email: "", name: "", phone: "", caregiver_type: invite.caregiver_type });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white border-2 border-emerald-200 rounded-2xl p-6" data-testid="caregivers-panel">
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-5 h-5 text-emerald-700" />
        <h2 className="font-serif text-xl text-brand-deep">Caregivers &amp; certifications</h2>
        <button onClick={load} className="ml-auto text-xs text-emerald-700 hover:underline" data-testid="cg-refresh">Refresh</button>
      </div>

      {/* Expiring alerts */}
      {expiring.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="cert-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-semibold text-amber-900">
              {expiring.length} certification{expiring.length === 1 ? "" : "s"} expiring in ≤30 days
            </span>
          </div>
          <ul className="space-y-1 text-xs text-amber-900">
            {expiring.slice(0, 6).map((x) => (
              <li key={x.cert_id} data-testid={`alert-${x.cert_id}`}>
                <span className="font-semibold">{x.caregiver_name}</span> ({x.caregiver_type}) · {x.cert_name} · {x.expired ? "EXPIRED " : "expires "} {x.expires_at}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Invite form */}
      <form onSubmit={submitInvite} className="bg-brand-bg/60 border border-brand-navy/10 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="cg-invite-form">
        <input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })}
          placeholder="Full name" data-testid="cg-invite-name"
          className="px-3 py-2 text-sm rounded border border-brand-navy/15" />
        <input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
          placeholder="Email" type="email" data-testid="cg-invite-email"
          className="px-3 py-2 text-sm rounded border border-brand-navy/15" />
        <input value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })}
          placeholder="Phone (optional)" data-testid="cg-invite-phone"
          className="px-3 py-2 text-sm rounded border border-brand-navy/15" />
        <select value={invite.caregiver_type} onChange={(e) => setInvite({ ...invite, caregiver_type: e.target.value })}
          data-testid="cg-invite-type"
          className="px-3 py-2 text-sm rounded border border-brand-navy/15">
          {CAREGIVER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button disabled={busy} data-testid="cg-invite-submit"
          className="inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium px-3 py-2 rounded-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add caregiver
        </button>
      </form>

      <div className="space-y-2" data-testid="caregivers-list">
        {loading && <div className="text-xs text-brand-muted italic">Loading…</div>}
        {!loading && list.length === 0 && <div className="text-xs text-brand-muted italic">No caregivers hired yet. Invite your first hire above.</div>}
        {list.map((cg) => <CaregiverRow key={cg.id} cg={cg} onChanged={load} />)}
      </div>
    </section>
  );
}

// ==================== Main dashboard ====================

export default function Admin() {
  const [authed, setAuthed] = useState(!!getTok());
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!getTok()) { setAuthed(false); return; }
    setLoading(true);
    try {
      const [overview, hc, meResp] = await Promise.all([
        api("/nemt/admin/dashboard/overview", {
          headers: { "X-Dispatcher-Auth": "Enterpr!Se", ...authHeader() }, // legacy compat until every endpoint moves
        }).catch(async () => api("/nemt/admin/dashboard/overview", { headers: authHeader() })),
        api("/homecare/admin/dashboard/overview", {
          headers: { "X-Dispatcher-Auth": "Enterpr!Se", ...authHeader() },
        }).catch(() => null),
        api("/admin/auth/me"),
      ]);
      setData({ ...overview, homecare: hc }); setMe(meResp);
    } catch (err) {
      if (String(err.message).includes("401")) { clearTok(); setAuthed(false); }
      else toast.error(err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const logout = () => { clearTok(); setAuthed(false); setData(null); setMe(null); };

  if (!authed) return <AuthGate onAuthed={() => setAuthed(true)} />;
  if (!data || !me) {
    return <div className="min-h-screen bg-brand-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>;
  }

  const t = data.today || {};
  const maxDayCount = Math.max(...(data.by_day || []).map((d) => d.count), 1);
  const canManage = (me.permissions || []).includes("admin.manage");

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      <header className="bg-brand-deep text-white px-6 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl">Owner dashboard</h1>
            <p className="text-xs uppercase tracking-[0.18em] opacity-70 mt-1">
              Live operational overview · {new Date(data.generated_at).toLocaleTimeString()}
              {me.user?.name ? ` · ${me.user.name} (${(me.user.role || "").toUpperCase()})` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} disabled={loading} data-testid="admin-refresh"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
            </button>
            <button
              onClick={async () => {
                try {
                  const tok = getTok();
                  const res = await fetch(`${API}/admin/manuals/features.pdf`, {
                    headers: { Authorization: `Bearer ${tok}` },
                  });
                  if (!res.ok) throw new Error((await res.json()).detail || `Server ${res.status}`);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = "1st_Touch_What_Is_Live.pdf";
                  a.click(); URL.revokeObjectURL(url);
                } catch (e) { toast.error(e.message || "Download failed"); }
              }}
              data-testid="admin-features-download"
              title="Plain-English guide to every feature currently live in production"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              <Star className="w-4 h-4" /> What's live
            </button>
            <button
              onClick={async (kind) => {
                try {
                  const tok = getTok();
                  const res = await fetch(`${API}/admin/manuals/admin.pdf`, {
                    headers: { Authorization: `Bearer ${tok}` },
                  });
                  if (!res.ok) throw new Error((await res.json()).detail || `Server ${res.status}`);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = "1st_Touch_Owner_Manual.pdf";
                  a.click(); URL.revokeObjectURL(url);
                } catch (e) { toast.error(e.message || "Manual download failed"); }
              }}
              data-testid="admin-manual-download"
              title="Download the current owner manual as a branded PDF"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              <Download className="w-4 h-4" /> Owner manual
            </button>
            <a href="/dispatcher" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              Dispatcher <ChevronRight className="w-4 h-4" />
            </a>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Real-time snapshot — click to drill in */}
        {data.realtime && (
          <section className="bg-brand-deep text-white rounded-2xl p-5" data-testid="realtime-strip">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-emerald-300" />
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold">Live right now</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <a href="/dispatcher" className="hover:opacity-80" data-testid="rt-held">
                <div className="text-3xl font-serif">{data.realtime.held_now}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] opacity-75">Rides held (paid, ready to assign)</div>
              </a>
              <a href="/dispatcher" className="hover:opacity-80" data-testid="rt-pending">
                <div className="text-3xl font-serif text-amber-200">{data.realtime.pending_payment_now}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] opacity-75">Waiting on card authorization</div>
              </a>
              <a href="/dispatcher" className="hover:opacity-80" data-testid="rt-inprogress">
                <div className="text-3xl font-serif text-emerald-200">{data.realtime.in_progress_now}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] opacity-75">Trips in motion (drivers on-route)</div>
              </a>
            </div>
          </section>
        )}

        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-muted mb-3">Today</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard testid="today-rides" label="Rides today" value={t.rides || 0} sub={`${t.held || 0} held · ${t.pending_payment || 0} pending`} />
            <StatCard testid="today-completed" label="Completed" value={t.completed || 0} tone="green" />
            <StatCard testid="today-cancelled" label="Cancelled" value={t.cancelled || 0} tone={t.cancelled ? "amber" : "default"} />
            <StatCard testid="drivers-active" label="Drivers on shift" value={`${data.drivers_active}/${data.drivers_total || "—"}`} sub="Location shared last 30m" />
            <StatCard testid="action-needed" label="Action needed" value={data.action_needed_count || 0}
              tone={data.action_needed_count ? "red" : "default"}
              sub={data.action_needed_count ? "Rides blocking new bookings" : "All clear"} />
          </div>
        </section>

        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-muted mb-3">Financials</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard testid="revenue-month" label="Revenue captured this month" value={money(data.revenue_this_month_cents)}
              sub={`${data.revenue_this_month_count} completed rides`} tone="green" />
            <StatCard testid="outstanding" label="Outstanding balances" value={money(data.outstanding_balance_cents)}
              sub={`${data.outstanding_ride_count} unresolved ride${data.outstanding_ride_count === 1 ? "" : "s"}`}
              tone={data.outstanding_balance_cents ? "red" : "default"} />
            <StatCard testid="avg-fare" label="Avg fare (this month)" value={
              data.revenue_this_month_count ? money(Math.round(data.revenue_this_month_cents / data.revenue_this_month_count)) : "—"
            } />
          </div>
        </section>

        {(data.by_day || []).length > 0 && (
          <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="rides-by-day">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl text-brand-deep">Rides · last {data.days} days</h2>
              <TrendingUp className="w-5 h-5 text-brand-green" />
            </div>
            <div className="flex items-end gap-1 h-32 border-b border-brand-navy/10">
              {(data.by_day || []).map((d) => (
                <div key={d.date} className="flex-1 flex flex-col justify-end items-center gap-1 group h-full"
                  title={`${d.date}: ${d.count} rides · ${money(d.revenue_cents)}`}>
                  <div className="w-full bg-brand-green/80 hover:bg-brand-green rounded-t transition"
                    style={{ height: `${Math.max((d.count / maxDayCount) * 100, 4)}%` }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-brand-muted mt-2">
              <span>{(data.by_day || [])[0]?.date}</span>
              <span>{(data.by_day || [])[(data.by_day || []).length - 1]?.date}</span>
            </div>
          </section>
        )}

        {(data.top_pickup_zones || []).length > 0 && (
          <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="top-zones">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl text-brand-deep">Top pickup zones · last {data.days} days</h2>
              <MapPin className="w-5 h-5 text-brand-green" />
            </div>
            <ul className="space-y-2">
              {data.top_pickup_zones.map((z, i) => (
                <li key={z.city} className="flex items-center justify-between text-sm border-b border-brand-navy/5 pb-2 last:border-0">
                  <span className="text-brand-deep"><span className="text-brand-muted mr-2">#{i + 1}</span>{z.city}</span>
                  <span className="font-semibold text-brand-green">{z.count} rides</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(data.top_drivers || []).length > 0 && (
          <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="top-drivers">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-serif text-xl text-brand-deep">Top drivers · last {data.days} days</h2>
                <p className="text-xs text-brand-muted">Ranked by revenue driven</p>
              </div>
              <Star className="w-5 h-5 text-brand-green" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.14em] text-brand-muted border-b border-brand-navy/10">
                  <tr>
                    <th className="text-left py-2">#</th>
                    <th className="text-left py-2">Driver</th>
                    <th className="text-right py-2">Rides</th>
                    <th className="text-right py-2">Revenue</th>
                    <th className="text-right py-2">Avg fare</th>
                    <th className="text-right py-2">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_drivers.map((d, i) => (
                    <tr key={d.driver_id} className="border-b border-brand-navy/5 last:border-0">
                      <td className="py-2 text-brand-muted">#{i + 1}</td>
                      <td className="py-2 text-brand-deep font-medium">{d.driver_name}</td>
                      <td className="py-2 text-right">{d.rides}</td>
                      <td className="py-2 text-right text-emerald-800 font-semibold">{money(d.revenue_cents)}</td>
                      <td className="py-2 text-right text-brand-muted">{d.rides ? money(Math.round(d.revenue_cents / d.rides)) : "—"}</td>
                      <td className="py-2 text-right">
                        {d.avg_rating ? (
                          <span className="inline-flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {d.avg_rating.toFixed(2)}
                            <span className="text-brand-muted text-[10px]">({d.rating_count})</span>
                          </span>
                        ) : <span className="text-brand-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {(data.trip_classes || []).length > 0 && (
          <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="trip-class-breakdown">
            <h2 className="font-serif text-xl text-brand-deep mb-4">Revenue by trip type · last {data.days} days</h2>
            <ul className="space-y-2">
              {data.trip_classes.map((c) => {
                const totalRev = data.trip_classes.reduce((s, x) => s + x.revenue_cents, 0) || 1;
                const pct = (c.revenue_cents / totalRev) * 100;
                return (
                  <li key={c.trip_class} className="text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="capitalize text-brand-deep font-medium">{c.trip_class.replace(/_/g, " ")}</span>
                      <span className="text-brand-muted">{c.count} rides · <b className="text-emerald-800">{money(c.revenue_cents)}</b></span>
                    </div>
                    <div className="w-full bg-brand-navy/10 rounded-full h-2">
                      <div className="bg-brand-green h-2 rounded-full transition" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
            {data.totals_window && (
              <div className="mt-4 grid grid-cols-3 gap-3 text-center border-t border-brand-navy/10 pt-4">
                <div><div className="font-serif text-2xl text-emerald-700">{data.totals_window.completed}</div><div className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">Completed</div></div>
                <div><div className="font-serif text-2xl text-amber-700">{data.totals_window.cancellation_rate}%</div><div className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">Cancellation rate</div></div>
                <div><div className="font-serif text-2xl text-red-700">{data.totals_window.no_show_rate}%</div><div className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">No-show rate</div></div>
              </div>
            )}
          </section>
        )}

        {canManage && <UsersPanel me={me} onReload={load} />}

        {/* Home Care business line — parallel to NEMT */}
        {data.homecare && (
          <section className="bg-white border-2 border-emerald-200 rounded-2xl p-6" data-testid="homecare-section">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <UserIcon className="w-4 h-4 text-emerald-700" />
              </div>
              <div>
                <h2 className="font-serif text-xl text-brand-deep">Home Care</h2>
                <p className="text-xs text-brand-muted">Nurses, aides, therapists · a parallel business line</p>
              </div>
              <a href="/homecare" className="ml-auto text-xs text-emerald-700 hover:underline">Client portal →</a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard testid="hc-requested" label="Visits requested" value={data.homecare.requested_visits || 0} tone={data.homecare.requested_visits ? "amber" : "default"} sub="Need dispatch to assign" />
              <StatCard testid="hc-scheduled" label="Visits scheduled" value={data.homecare.scheduled_visits || 0} tone="green" />
              <StatCard testid="hc-completed-today" label="Completed today" value={data.homecare.completed_today || 0} />
              <StatCard testid="hc-caregivers" label="Caregivers active" value={data.homecare.caregivers_total || 0}
                sub={(data.homecare.caregivers_by_type || []).filter(x => x.count).map(x => `${x.count} ${x.type}`).join(" · ") || "None hired yet"} />
              <StatCard testid="hc-revenue-month" label="Revenue this month" value={money(data.homecare.revenue_this_month_cents)} tone="green"
                sub={`${data.homecare.revenue_this_month_count} visits`} />
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-7 gap-2 text-center">
              {(data.homecare.caregivers_by_type || []).map((t) => (
                <div key={t.type} className="bg-brand-bg rounded-lg p-2 border border-brand-navy/5">
                  <div className="font-serif text-lg text-brand-deep">{t.count}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">{t.type}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Caregiver management (invite, PWA link, certifications, expiring alerts) */}
        <CaregiversPanel />
      </main>
    </div>
  );
}
